import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const values = [
  { title: "Discipline", body: "Doing what is needed when motivation is absent." },
  { title: "Resilience", body: "Continuing without pretending the process is easy." },
  { title: "Honesty", body: "Recognising where we are and speaking openly." },
  { title: "Adjustment", body: "Changing the approach without abandoning the objective." },
  { title: "Community", body: "Helping others understand they are not facing their battles alone." },
];

export default function About() {
  return (
    <div className="bg-[#F4F3EF]">
      {/* Hero */}
      <section className="relative bg-[#0A0A0A] text-white grain overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-jt897jd0.emergentagent.net/job_down-test-adjust/artifacts/0dcczfaj_ChatGPT%20Image%20Jul%2025%2C%202026%20at%2002_42_44%20PM.png"
            alt="Runner in DTA jacket at dawn"
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/85" />
        </div>
        <div className="relative dta-container pt-32 pb-24 md:pb-32">
          <p className="dta-overline text-white/60">About DTA</p>
          <h1 className="hero-headline text-5xl md:text-8xl mt-6 max-w-5xl">
            Creating a clearer<br />mindset and stronger<br />resilience.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
            DTA is a British clothing brand built around a single principle borrowed from the marksmanship principals, and rebuilt for everyday life.
          </p>
        </div>
      </section>

      {/* The training principle */}
      <section className="py-24 md:py-32 border-b border-black/10">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="dta-overline text-navy md:sticky md:top-28">The training principle</p>
          </div>
          <div className="md:col-span-8 space-y-6 max-w-2xl text-[#272727]">
            <h2 className="section-heading text-4xl md:text-6xl text-[#0A0A0A]">
              Change requires<br />a challenge.
            </h2>
            <p className="text-lg leading-relaxed">
              Whether your goal is to build muscle, become stronger, lose body fat or completely change the way you look and feel, one principle sits at the heart of progress:
            </p>
            <p className="font-display uppercase font-bold text-xl md:text-2xl text-[#031D47]">
              Your body has to be given a reason to change.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              If we continually do what feels comfortable, our body has very little reason to adapt. To become stronger, fitter and more capable, we need to progressively challenge ourselves beyond what we could comfortably do before.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              In resistance training, that means learning to work towards muscular failure.
            </p>

            <h3 className="font-display uppercase font-bold text-3xl md:text-4xl text-[#0A0A0A] pt-6">
              What does failure actually mean?
            </h3>
            <div className="space-y-2">
              <p className="text-base leading-relaxed opacity-80">
                Failure doesn&rsquo;t mean collapsing on the gym floor.
              </p>
              <p className="text-base leading-relaxed opacity-80">
                It doesn&rsquo;t mean training recklessly.
              </p>
              <p className="text-base leading-relaxed opacity-80">
                And it doesn&rsquo;t mean lifting the heaviest weight you can find.
              </p>
            </div>
            <p className="font-display uppercase font-bold text-xl md:text-2xl text-[#0A0A0A]">
              Muscular failure is simply the point where your muscles can no longer complete another repetition with good technique.
            </p>

            <div className="pt-4 pb-2 border-l-2 border-[#031D47] pl-5 space-y-2 bg-white/40">
              <p className="text-base leading-relaxed opacity-85">
                Imagine you are performing an exercise for 10 repetitions.
              </p>
              <ul className="space-y-1.5 text-base leading-relaxed opacity-85">
                <li><span className="dta-overline text-[0.7rem] text-navy mr-2">Reps 1&ndash;5</span> might feel relatively comfortable.</li>
                <li><span className="dta-overline text-[0.7rem] text-navy mr-2">Reps 6&ndash;8</span> begin to demand effort.</li>
                <li><span className="dta-overline text-[0.7rem] text-navy mr-2">Rep 9</span> is difficult.</li>
                <li><span className="dta-overline text-[0.7rem] text-navy mr-2">Rep 10</span> takes real determination.</li>
              </ul>
              <p className="text-base leading-relaxed opacity-85 pt-2">
                And when you attempt another repetition, you simply cannot complete it while maintaining good form.
              </p>
              <p className="font-display uppercase font-bold text-lg md:text-xl text-[#031D47] pt-1">
                That&rsquo;s muscular failure.
              </p>
            </div>

            <p className="text-base leading-relaxed opacity-80 pt-2">It tells the body:</p>
            <blockquote className="font-display italic text-lg md:text-xl text-[#0A0A0A] border-l-2 border-[#031D47] pl-5 py-1">
              &ldquo;What you can currently do isn&rsquo;t enough for what I&rsquo;m asking you to do.&rdquo;
            </blockquote>
            <p className="font-display uppercase font-bold text-lg md:text-xl text-[#031D47]">
              The body responds by adapting.
            </p>

            <div className="pt-6 space-y-8 border-t border-black/10">
              <div>
                <h4 className="font-display uppercase font-bold text-2xl md:text-3xl text-[#0A0A0A]">
                  Build muscle.
                </h4>
                <p className="text-base leading-relaxed opacity-80 mt-3">
                  When we repeatedly challenge a muscle through resistance training, we create a stimulus for it to adapt.
                </p>
                <p className="text-base leading-relaxed opacity-80 mt-2">
                  With the right training, sufficient protein, good nutrition and adequate recovery, those muscles can become larger.
                </p>
                <p className="font-display uppercase font-bold text-lg md:text-xl text-[#031D47] mt-3">
                  This process is called muscular hypertrophy.
                </p>
              </div>

              <div>
                <h4 className="font-display uppercase font-bold text-2xl md:text-3xl text-[#0A0A0A]">
                  Build strength.
                </h4>
                <p className="text-base leading-relaxed opacity-80 mt-3">
                  Strength develops when we progressively ask the body to do more.
                </p>
                <p className="text-base leading-relaxed opacity-80 mt-2">That might mean:</p>
                <ul className="text-base leading-relaxed opacity-80 mt-2 list-disc pl-5 space-y-1">
                  <li>More weight.</li>
                  <li>More repetitions.</li>
                  <li>Better technique.</li>
                  <li>Greater control.</li>
                  <li>More challenging exercises.</li>
                </ul>
                <p className="text-base leading-relaxed opacity-80 mt-3">
                  Over time, your muscles and nervous system adapt, allowing you to perform work that previously felt impossible.
                </p>
                <p className="font-display uppercase font-bold text-lg md:text-xl text-[#031D47] mt-3">
                  This is known as progressive overload.
                </p>
              </div>

              <div>
                <h4 className="font-display uppercase font-bold text-2xl md:text-3xl text-[#0A0A0A]">
                  Lose body fat.
                </h4>
                <p className="text-base leading-relaxed opacity-80 mt-3">
                  Fat loss follows a slightly different process.
                </p>
                <p className="text-base leading-relaxed opacity-80 mt-2">
                  You don&rsquo;t need to train every exercise to failure to lose weight. Fat loss primarily occurs when your body uses more energy than it consumes over time.
                </p>
                <p className="text-base leading-relaxed opacity-80 mt-2">
                  That&rsquo;s where calories, nutrition and your macros become important.
                </p>
                <p className="text-base leading-relaxed opacity-80 mt-2">
                  Resistance training then helps us protect &mdash; and potentially build &mdash; muscle while we reduce body fat.
                </p>
                <p className="font-display uppercase font-bold text-lg md:text-xl text-[#0A0A0A] mt-3">
                  The objective isn&rsquo;t simply to become lighter.
                </p>
                <p className="font-display uppercase font-bold text-lg md:text-xl text-[#031D47] mt-1">
                  It&rsquo;s to improve your body composition.
                </p>
                <ul className="text-base leading-relaxed opacity-80 mt-3 space-y-1">
                  <li>Less unnecessary body fat.</li>
                  <li>More functional muscle.</li>
                  <li>More strength.</li>
                  <li>Better fitness.</li>
                  <li>Better performance.</li>
                </ul>
              </div>
            </div>

            <h3 className="font-display uppercase font-bold text-3xl md:text-4xl text-[#0A0A0A] pt-8">
              The DTA approach.
            </h3>
            <p className="text-base leading-relaxed opacity-80">
              At DTA, we&rsquo;re not interested in endless workouts with no purpose.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              We want every session to create a reason for your body to adapt.
            </p>
            <div className="pt-2 space-y-1 font-display uppercase font-bold text-xl md:text-2xl">
              <p>We train with intent.</p>
              <p>We monitor the results.</p>
              <p>We adjust when necessary.</p>
              <p className="text-[#031D47]">And we progressively ask more from ourselves.</p>
            </div>
            <p className="text-base leading-relaxed opacity-80 pt-4">
              Because eventually you reach a point where what once pushed you to failure becomes something you can comfortably achieve.
            </p>
            <p className="font-display uppercase font-bold text-xl md:text-2xl text-[#031D47]">
              That&rsquo;s progress.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              Then we adjust the target and go again.
            </p>

            <p className="pt-6 font-display uppercase font-black text-3xl md:text-4xl tracking-tight border-t border-black/10 mt-4">
              Down. <span className="text-[#031D47]">Test.</span> Adjust.
            </p>
            <p className="font-display uppercase font-bold text-lg md:text-xl text-[#0A0A0A]">
              Challenge the body. Measure the response. Make the adjustment. Repeat.
            </p>
          </div>
        </div>
      </section>

      {/* From marksmanship to mindset */}
      <section className="py-24 md:py-32 bg-[#0A0A0A] text-white">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="dta-overline text-white/50">From marksmanship to mindset</p>
            <h2 className="section-heading text-4xl md:text-6xl mt-4">
              Not every<br />attempt succeeds.
            </h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 text-white/80">
            {[
              { h: "Failure provides information", b: "It shows you where the process broke down. That is worth more than pretending it did not happen." },
              { h: "Assessment creates understanding", b: "Test honestly. Question the pattern. Speak plainly with yourself and with others." },
              { h: "Adjustment creates progress", b: "Small changes to grip, position, mindset or approach. The objective does not need to change." },
              { h: "Trying again is resilience", b: "Not silent endurance. A considered, informed second attempt." },
            ].map((v) => (
              <div key={v.h} className="border-t border-white/15 pt-5">
                <h3 className="font-display uppercase text-xl md:text-2xl font-bold text-[#8fa8d6]">
                  {v.h}
                </h3>
                <p className="text-sm mt-3 leading-relaxed">{v.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Military heritage, civilian world */}
      <section className="py-24 md:py-32">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative min-h-[420px] md:min-h-[560px] overflow-hidden">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_down-test-adjust/artifacts/5knwmx0m_ChatGPT%20Image%20Jul%2025%2C%202026%20at%2004_07_01%20PM.png"
              alt="Battle rope training in DTA kit"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-6 space-y-6">
            <p className="dta-overline text-navy">Military heritage · Civilian world</p>
            <h2 className="section-heading text-4xl md:text-6xl">
              Join the DTA revolution<br />and let&apos;s keep moving forward.
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#272727]">
              The brand is shaped by military experience but is designed for everyone. We don&apos;t ask what uniform you wore, or whether you served at all. The principle belongs to anyone prepared to face where they are, make an adjustment and continue moving forward.
            </p>
          </div>
        </div>
      </section>

      {/* Founder's story */}
      <section className="py-24 md:py-32 bg-[#F4F3EF] border-y border-black/10">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="dta-overline text-navy">Founder&apos;s story</p>
          </div>
          <div className="md:col-span-8 max-w-2xl space-y-6 text-[#272727]">
            <h2 className="section-heading text-4xl md:text-6xl text-[#0A0A0A]">
              Built by someone<br />who has adjusted.
            </h2>

            <p className="text-lg leading-relaxed">
              Back in May 2022, I could feel the cracks beginning to appear. On the outside, I was smiling and carrying on as normal, but inside my head, it felt as though a grenade had exploded.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              I had reached a point where I had almost had enough. Dark thoughts were circling through my mind, and I kept asking myself why I felt so down all the time. I constantly found myself asking, &ldquo;Why is this happening to me?&rdquo;
            </p>

            <p className="font-display uppercase font-bold text-2xl md:text-3xl text-[#031D47] pt-2">
              Then something shifted.
            </p>

            <p className="text-base leading-relaxed opacity-80">
              I began to see it differently. I spoke to my mates and my colleagues who had also seen a shift in how I was acting towards them, and then to my family who confirmed it. One of my close friends got in touch with my OC, the wheels were greased, and I started to get the help I needed.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              It was while receiving the help that I started to look at it &ldquo;as a test.&rdquo; Perhaps there was still something I was meant to do, something I was meant to become, and this experience was preparing me for it.
            </p>

            <p className="font-display uppercase font-bold text-2xl md:text-3xl text-[#0A0A0A] pt-2">
              That was the moment I understood that failure is part of the process.
            </p>

            <p className="text-base leading-relaxed opacity-80">
              To move forward, we sometimes have to stop looking back and adjust our perspective. We have to acknowledge what has happened, learn from it, adjust our direction, and continue.
            </p>
            <p className="text-base leading-relaxed opacity-80">
              That is where the concept for DTA was born. Drawing on a military marksmanship acronym, <strong>Down. Test. Adjust.</strong> it came to represent more than a shooting principle.
            </p>

            <p className="text-lg leading-relaxed pt-2 border-t border-black/10 mt-4">
              For me, it marked the beginning of a new chapter, a renewed sense of purpose, and the belief that being knocked down does not mean being defeated.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32">
        <div className="dta-container">
          <p className="dta-overline text-navy">Brand values</p>
          <h2 className="section-heading text-4xl md:text-6xl mt-4 max-w-3xl">
            Five words we wear<br />like a standard.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-5 border-y border-black/10">
            {values.map((v, i) => (
              <div
                key={v.title}
                className={`p-6 md:p-8 ${
                  i > 0 ? "border-t md:border-t-0 md:border-l border-black/10" : ""
                }`}
              >
                <span className="dta-overline text-navy text-[0.65rem]">0{i + 1}</span>
                <h3 className="font-display uppercase font-bold text-3xl md:text-4xl mt-4">{v.title}</h3>
                <p className="text-sm text-[#272727] mt-3 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A] text-white">
        <div className="dta-container flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="section-heading text-4xl md:text-5xl max-w-xl">
            The mission has not changed.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/store" className="dta-btn dta-btn-primary">
              Shop DTA <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link to="/support" className="dta-btn dta-btn-outline-light">
              Find Support
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
